"use client"

import { useState, useMemo, useEffect } from "react"
import { format, getDaysInMonth, startOfMonth, addMonths, subMonths } from "date-fns"
import { ChevronLeft, ChevronRight, Clock, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { FacilitySlot } from "@/types/facility/booking"
import { FacilitySchedule } from "@/types/facility/schedule"

interface ExtendedMember {
  id: number;
  membership_plans?: Array<{
    membership_plan_id: number;
    membership_plan_name: string;
    status: string;
  }>;
}

interface SlotSelectorProps {
  slots: FacilitySlot[]
  selectedSlot: FacilitySlot | null
  onSlotSelect: (slot: FacilitySlot) => void
  isLoading?: boolean
  action?: "add" | "edit"
  selectedMember?: ExtendedMember | null
  scheduleData?: FacilitySchedule
}

export function SlotSelector({
  slots,
  selectedSlot,
  onSlotSelect,
  isLoading = false,
  action = "add",
  selectedMember,
  scheduleData,
}: SlotSelectorProps) {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [selectedDate, setSelectedDate] = useState<string>("")
  const [isOpen, setIsOpen] = useState(false)
  const [viewMode, setViewMode] = useState<"calendar" | "slots">("calendar")

  // Initialize component state when dropdown opens
  useEffect(() => {
    if (isOpen) {
      if (selectedSlot && slots.length > 0) {
        // Find the slot in the slots array to ensure it exists
        const foundSlot = slots.find(slot => slot.id === selectedSlot.id)
        if (foundSlot) {
          try {
            // Use consistent date formatting with error handling
            const slotDate = format(new Date(foundSlot.date), "yyyy-MM-dd")
            setSelectedDate(slotDate)
            setViewMode("slots")
          } catch (error) {
            console.warn('Error formatting slot date:', foundSlot.date, error)
            setSelectedDate("")
            setViewMode("calendar")
          }
        } else {
          setSelectedDate("")
          setViewMode("calendar")
        }
      } else {
        setSelectedDate("")
        setViewMode("calendar")
      }
    }
  }, [isOpen, selectedSlot, slots])

  // Update current month when slots are loaded (for future dates)
  useEffect(() => {
    if (slots.length > 0) {
      try {
        const firstSlotDate = new Date(slots[0].date)
        if (firstSlotDate.getFullYear() > new Date().getFullYear() ||
            (firstSlotDate.getFullYear() === new Date().getFullYear() &&
             firstSlotDate.getMonth() > new Date().getMonth())) {
          setCurrentMonth(firstSlotDate)
        }
      } catch (error) {
        console.warn('Error updating current month from slots:', error)
      }
    }
  }, [slots])

  // Reset state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDate("")
      setViewMode("calendar")
    }
  }, [isOpen])

  // Helper function to calculate effective price for a slot
  const calculateSlotPrice = (slot: FacilitySlot) => {
    // Priority 1: Special Price (if slot date falls within special pricing range)
    if (slot.special_pricing && slot.special_pricing.length > 0) {
      try {
        const slotDateStr = format(new Date(slot.date), 'yyyy-MM-dd');
        const matchingSpecialPrice = slot.special_pricing.find((specialPrice) => {
          return slotDateStr >= specialPrice.date_from && slotDateStr <= specialPrice.date_to;
        });
        if (matchingSpecialPrice) {
          return {
            price: matchingSpecialPrice.price,
            type: 'special',
            name: matchingSpecialPrice.name
          };
        }
      } catch (error) {
        console.warn('Error calculating special price for slot:', slot.id, error);
      }
    }

    // Priority 2: Plan-based Price (if member has matching membership plan)
    if (selectedMember && slot.plan_pricing && slot.plan_pricing.length > 0) {
      const memberPlans = selectedMember.membership_plans?.filter(plan => plan.status === 'active') || [];
      if (memberPlans.length > 0) {
        // Find the first matching plan pricing (as per user requirement)
        for (const memberPlan of memberPlans) {
          if (!memberPlan || typeof memberPlan.membership_plan_id === 'undefined') continue;
          const matchingPlanPrice = slot.plan_pricing.find(planPrice =>
            Number(planPrice.membership_plan_id) === Number(memberPlan.membership_plan_id)
          );
          if (matchingPlanPrice) {
            return {
              price: matchingPlanPrice.price,
              type: 'plan',
              name: memberPlan.membership_plan_name
            };
          }
        }
      }
    }

    // Priority 3: Schedule Base Price
    return {
      price: scheduleData?.general_info?.price || 0,
      type: 'base',
      name: null
    };
  };

  const slotsByDate = useMemo(() => {
    const map: Record<string, FacilitySlot[]> = {}
    const validSlots = slots
      .filter((slot) => (action === "add" ? slot.is_available : true))
      .filter((slot) => {
        // Additional validation to ensure slot has all required fields
        if (!slot.date || !slot.start_time || !slot.end_time) {
          console.warn('Slot missing required fields:', slot)
          return false
        }

        // Validate date format - slot.date is already in ISO format like "2025-10-26T00:00:00"
        try {
          const testDate = new Date(slot.date)
          if (isNaN(testDate.getTime())) {
            console.warn('Slot with invalid date:', slot.date, slot)
            return false
          }
          return true
        } catch (error) {
          console.warn('Error validating slot date:', slot.date, error)
          return false
        }
      })

    validSlots.forEach((slot) => {
      try {
        // Parse the date correctly - slot.date already has timezone info
        let dateToFormat: Date
        try {
          dateToFormat = new Date(slot.date)
          if (isNaN(dateToFormat.getTime())) {
            console.warn('Invalid date:', slot.date, slot)
            return
          }
        } catch (error) {
          console.warn('Error creating date:', slot.date, error)
          return
        }

        const dateKey = format(dateToFormat, "yyyy-MM-dd")
        if (!map[dateKey]) {
          map[dateKey] = []
        }
        map[dateKey].push(slot)
      } catch (error) {
        console.warn('Error processing slot:', slot, error)
      }
    })

    return map
  }, [slots, action, currentMonth])

  // Get slots for selected date
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return []

    // Find slots for the selected date
    let slots = slotsByDate[selectedDate] || []

    // If in edit mode and we have a selectedSlot but no slots for the selected date,
    // it might be a timing issue, so try to find the slot in all available slots
    if (slots.length === 0 && selectedSlot && action === "edit") {
      // Check if the selectedSlot matches the selected date
      try {
        const selectedSlotDate = format(new Date(selectedSlot.date), "yyyy-MM-dd")
        if (selectedSlotDate === selectedDate) {
          slots = [selectedSlot]
        }
      } catch (error) {
        console.warn('Error formatting selected slot date:', selectedSlot.date, error)
      }
    }

    return slots
  }, [selectedDate, slotsByDate, selectedSlot, action])

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const firstDay = startOfMonth(currentMonth)
    const daysInMonth = getDaysInMonth(currentMonth)
    const startingDayOfWeek = firstDay.getDay()

    const days: (Date | null)[] = []

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i))
    }

    return days
  }, [currentMonth])

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
    setSelectedDate("")
  }

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
    setSelectedDate("")
  }

  const handleDateClick = (date: Date) => {
    try {
      // Use consistent date formatting - date from calendar is already a Date object
      const dateKey = format(date, "yyyy-MM-dd")
      setSelectedDate(dateKey)
      setViewMode("slots")
    } catch (error) {
      console.warn('Error handling date click:', date, error)
    }
  }

  const handleBackToCalendar = () => {
    setViewMode("calendar")
  }

  const handleSlotSelect = (slot: FacilitySlot) => {
    onSlotSelect(slot)
    setIsOpen(false)
  }

  if (isLoading) {
    return <div className="text-xs text-gray-500">Loading slots...</div>
  }

  if (slots.length === 0) {
    return <div className="text-xs text-gray-500">No available slots</div>
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full justify-between text-xs h-9 bg-transparent px-3 text-left font-medium">
          <span className="truncate ">
            {selectedSlot
              ? (() => {
                  try {
                    return `${format(new Date(selectedSlot.date), "EEEE, MMM dd, yyyy")} at ${selectedSlot.start_time || "TBD"} - ${selectedSlot.end_time || "TBD"}`
                  } catch (error) {
                    console.warn('Error formatting trigger date:', selectedSlot.date, error)
                    return "Invalid date selected"
                  }
                })()
              : "Select a time slot"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72 p-3">
        <div className="space-y-3">
          {viewMode === "calendar" ? (
            <>
              {/* Month Navigation */}
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={handlePrevMonth} className="h-6 w-6 p-0">
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <h2 className="text-xs font-medium">{format(currentMonth, "MMM yyyy")}</h2>
                <Button variant="ghost" size="sm" onClick={handleNextMonth} className="h-6 w-6 p-0">
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>

              <div className="grid grid-cols-7 gap-1">
              {["S", "M", "T", "W", "T", "F", "S"].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-1 px-1">
                    {day}
                  </div>
                ))}
                
                {calendarDays.map((date, idx) => {
                  if (!date) {
                    return <div key={`empty-${idx}`} className="h-8" />
                  }

                  // Use consistent date formatting for key and comparison
                  // Calendar dates are in local timezone, slots are in the provided format
                  const dateKey = format(date, "yyyy-MM-dd")
                  const hasSlots = slotsByDate[dateKey]?.length > 0
                  const isSelected = selectedDate === dateKey

                  // Check if this date contains the selected slot (for edit mode highlighting)
                  const isSelectedSlotDate = selectedSlot ?
                    (() => {
                      try {
                        return format(new Date(selectedSlot.date), "yyyy-MM-dd") === dateKey
                      } catch (error) {
                        console.warn('Error checking selected slot date:', selectedSlot.date, error)
                        return false
                      }
                    })() : false

                  return (
                    <button
                      key={dateKey}
                      onClick={() => handleDateClick(date)}
                      disabled={!hasSlots}
                      className={`
                        h-8 rounded text-xs font-medium transition-all flex items-center justify-center relative min-w-0
                        ${!hasSlots ? "bg-gray-100 text-gray-300 cursor-not-allowed" : ""}
                        ${isSelected || isSelectedSlotDate ? "ring-2 ring-blue-500 bg-blue-100 text-blue-900" : ""}
                        ${hasSlots && !isSelected && !isSelectedSlotDate ? "border border-gray-200 hover:bg-blue-50 text-gray-900 cursor-pointer" : ""}
                      `}
                    >
                      <span className="leading-none">{date.getDate()}</span>
                      {hasSlots && <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />}
                    </button>
                  )
                })}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <Button variant="ghost" size="sm" onClick={handleBackToCalendar} className="h-7 w-7 p-0">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {(() => {
                    try {
                      return format(new Date(selectedDate), "EEEE, MMM dd, yyyy")
                    } catch (error) {
                      console.warn('Error formatting header date:', selectedDate, error)
                      return "Invalid date"
                    }
                  })()}
                </div>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {slotsForSelectedDate.map((slot) => (
                  <button
                    key={slot.id}
                    onClick={() => handleSlotSelect(slot)}
                    className={`
                      w-full text-left p-3 rounded-md text-xs transition-all border flex flex-col gap-1
                      ${
                        selectedSlot?.id === slot.id
                          ? "border-blue-500 bg-blue-50 font-medium text-blue-900"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50 text-gray-900"
                      }
                    `}
                  >
                    <div className="font-medium flex items-center justify-between w-full">
                      <span className="flex-1 text-left min-w-0 overflow-hidden text-ellipsis">
                        {slot.start_time || "TBD"} - {slot.end_time || "TBD"}
                      </span>
                      {slot.is_exclusive_schedule && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2 flex-shrink-0">
                          Exclusive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center justify-between w-full">
                      <span>Duration: {slot.duration_hours}h</span>
                      {(() => {
                        const pricing = calculateSlotPrice(slot);
                        return (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`font-medium ${
                              pricing.type === 'special' ? 'text-orange-600' :
                              pricing.type === 'plan' ? 'text-blue-600' :
                              'text-green-600 hidden'
                            }`}>
                              Rs. {pricing.price.toLocaleString()}
                            </span>
                            {pricing.type === 'special' && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                                Special
                              </span>
                            )}
                            {pricing.type === 'plan' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                Plan
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
